import { useMemo, useRef, useState, type DragEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileUp, Upload } from "lucide-react";
import { toast } from "../../lib/toast";
import parsePhoneNumberFromString from "libphonenumber-js";
import { Badge } from "../../components/ui/Badge";
import { Select } from "../../components/ui/Select";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { api, apiErrorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";

interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

/** Minimal RFC-4180-aware parser: handles quoted fields and embedded commas/newlines, which a
 * naive split(",") mangles the moment a message body contains a comma. */
function parseCsv(text: string): ParsedCsv {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else inQuotes = false;
      } else field += char;
      continue;
    }
    if (char === '"') inQuotes = true;
    else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (char !== "\r") field += char;
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  const [headers = [], ...body] = rows.filter((r) => r.some((cell) => cell.trim() !== ""));
  return { headers, rows: body };
}

interface DryRunRow {
  recipient: string;
  normalized: string | null;
  message: string;
  error: string | null;
}

export function BulkUpload() {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [csv, setCsv] = useState<ParsedCsv | null>(null);
  const [fileName, setFileName] = useState("");
  const [phoneCol, setPhoneCol] = useState<number>(-1);
  const [messageCol, setMessageCol] = useState<number>(-1);
  const [dragging, setDragging] = useState(false);

  async function ingest(file: File) {
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.headers.length === 0) {
      toast.error("That file has no readable header row.");
      return;
    }
    setCsv(parsed);
    setFileName(file.name);
    // Pre-select the obvious columns so the common case needs no mapping at all.
    const guess = (candidates: string[]) =>
      parsed.headers.findIndex((h) => candidates.includes(h.trim().toLowerCase()));
    setPhoneCol(guess(["phone", "number", "recipient", "msisdn", "to"]));
    setMessageCol(guess(["message", "body", "text", "sms"]));
  }

  const dryRun: DryRunRow[] = useMemo(() => {
    if (!csv || phoneCol < 0) return [];
    return csv.rows.map((row) => {
      const recipient = (row[phoneCol] ?? "").trim();
      const message = messageCol >= 0 ? (row[messageCol] ?? "").trim() : "";
      const parsed = parsePhoneNumberFromString(recipient);
      const normalized = parsed?.isValid() ? parsed.number : null;
      let error: string | null = null;
      if (!recipient) error = "Missing phone number";
      else if (!normalized) error = "Not a valid phone number";
      else if (!message) error = "Missing message body";
      return { recipient, normalized, message, error };
    });
  }, [csv, phoneCol, messageCol]);

  const validRows = dryRun.filter((r) => !r.error);
  const invalidRows = dryRun.filter((r) => r.error);

  const sendMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ batchId: string; accepted: number }>("/gateway/send-bulk", {
          items: validRows.map((r) => ({ recipient: r.normalized, message: r.message })),
        })
      ).data,
    onSuccess: (result) => {
      toast.success(`Batch created — ${result.accepted} messages queued.`);
      setCsv(null);
      setFileName("");
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
      void queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Bulk send failed.")),
  });

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) void ingest(file);
  }

  if (!csv) {
    return (
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragOver={(e) => {
          // preventDefault on dragover is what tells the browser this element accepts a drop —
          // without it the browser navigates to the file instead of firing onDrop.
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          // Dragging across a child fires dragleave on the parent. Only clear the state when the
          // pointer has actually left the drop zone, or the highlight flickers as the cursor
          // crosses the icon and the text.
          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
          setDragging(false);
        }}
        onDrop={(e) => {
          setDragging(false);
          handleDrop(e);
        }}
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-card border-2 border-dashed px-6 py-16 text-center",
          "transition-colors duration-fast ease-standard",
          dragging
            ? "border-accent bg-accent-soft ring-2 ring-inset ring-accent/20"
            : "border-border",
        )}
      >
        <FileUp
          aria-hidden
          className={cn(
            "size-8 transition-transform duration-base ease-standard motion-reduce:transition-none",
            // A small lift on drag-over, so the zone reads as armed rather than merely tinted.
            dragging ? "scale-110 text-accent" : "text-text-muted",
          )}
        />
        <div>
          <p
            className={cn(
              "text-sm font-medium transition-colors duration-fast",
              dragging ? "text-accent" : "text-text",
            )}
          >
            {dragging ? "Drop the file to upload" : "Drop a CSV here"}
          </p>
          <p className="mt-1 text-sm text-text-muted">
            One row per message, with a phone column and a message column.
          </p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<Upload className="size-4" aria-hidden />}
          onClick={() => fileInput.current?.click()}
        >
          Choose file
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void ingest(file);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-text">{fileName}</p>
          <p className="text-xs text-text-muted">{csv.rows.length} data rows</p>
        </div>
        <Button
          variant="ghost"
          leftIcon={<FileUp className="size-4" aria-hidden />}
          onClick={() => {
            setCsv(null);
            setFileName("");
          }}
        >
          Choose a different file
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {[
          { id: "phone-col", label: "Phone column", value: phoneCol, set: setPhoneCol },
          { id: "message-col", label: "Message column", value: messageCol, set: setMessageCol },
        ].map(({ id, label, value, set }) => (
          <Select
            key={id}
            id={id}
            label={label}
            value={value}
            onChange={(e) => set(Number(e.target.value))}
          >
            <option value={-1}>Not mapped</option>
            {csv.headers.map((header, index) => (
              <option key={`${header}-${index}`} value={index}>
                {header === "" ? `Column ${index + 1}` : header}
              </option>
            ))}
          </Select>
        ))}
      </div>

      {phoneCol >= 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="success">{validRows.length} ready</Badge>
            {invalidRows.length > 0 && <Badge tone="danger">{invalidRows.length} with errors</Badge>}
          </div>

          <Card padding="sm" className="scrollbar-slim max-h-80 overflow-auto p-0">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-[1] bg-surface">
                <tr className="border-b border-border text-left">
                  <th className="px-3 py-2 font-medium text-text-muted">Recipient</th>
                  <th className="px-3 py-2 font-medium text-text-muted">Message</th>
                  <th className="px-3 py-2 font-medium text-text-muted">Status</th>
                </tr>
              </thead>
              <tbody>
                {dryRun.slice(0, 200).map((row, index) => (
                  <tr key={index} className="border-b border-border/50">
                    <td className="px-3 py-2 text-text">
                      {row.normalized ?? (row.recipient || "—")}
                    </td>
                    <td className="max-w-xs truncate px-3 py-2 text-text-muted">
                      {row.message || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {row.error ? (
                        <span className="text-danger">{row.error}</span>
                      ) : (
                        <span className="text-success">Ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {dryRun.length > 200 && (
            <p className="text-xs text-text-muted">
              Previewing the first 200 rows. All {dryRun.length} will be submitted.
            </p>
          )}

          <div>
            <Button
              size="lg"
              leftIcon={<Upload className="size-4" aria-hidden />}
              disabled={validRows.length === 0}
              loading={sendMutation.isPending}
              onClick={() => sendMutation.mutate()}
            >
              Send {validRows.length} message{validRows.length === 1 ? "" : "s"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
